import React, { useMemo, useState, useEffect, useRef } from "react";
import BusinessNeeds from "../components/BusinessNeeds";
import { useData } from "../../services/context/DataContext";
import FooterButtonHoc from "../../common/FooterButtonHoc";
import { BusinessNeed, CONTACT_INFO_INIT } from "../../services/types/LeadRequisitionType";
import { getBackSteps, getNextSteps } from "../../services/utils/utils";
import { INIT_STEPS } from "../../services/constants/InitialConstants";
import CustomAlert from "../components/CustomAlert";
import { handleScript } from "../../services/utils/utils";

const BusinessNeedsPage: React.FC = () => {
  const {
    steps,
    setSteps,
    businessNeedInfo,
    setBusinessNeedInfo,
    setIsEditMode,
    isEditMode,
    setIsAboutBusiness,
    setCompanyInfo,
    setContactInfo,
    setIsBasicInfo,
    setError,
    setIsCancelClicked,
    setTextAreaValue,
  } = useData();
  const currentStep = 3;
  const [errorCount, setErrorCount] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const { count, errors } = getErrorCount(businessNeedInfo?.businessNeeds);
    setErrorCount(count);
    setErrors(errors);
    if (count === 0) {
      setShowAlert(false);
    }
  }, [businessNeedInfo, errorCount]);

  const onSubmitHandler = () => {
    setIsSubmitClicked(true);
    if (enabledSubmit) {
      const updatedSteps = getNextSteps(steps, currentStep, isEditMode);
      setSteps(updatedSteps);
      setShowAlert(false);
      handleScript(divRef, "ccbContactUsFormStep3BusinessNeedsCompleted");
    } else {
      const { count, errors } = getErrorCount(businessNeedInfo?.businessNeeds);
      if (count) {        
        setShowAlert(true);
        setErrors(errors);
      }
    }
    if (window.parent.location.href.includes("contact-us")) window.scrollTo(0, 0);
  };

  const onCancelHandler = () => {
    setSteps(INIT_STEPS);
    setIsAboutBusiness(false);
    setBusinessNeedInfo(() => ({ businessNeeds: [] }));
    setContactInfo(CONTACT_INFO_INIT);
    setCompanyInfo(null);
    setIsEditMode(false);
    setIsBasicInfo(false);
    setError(null);
    setIsCancelClicked(true);
    setTextAreaValue([]);
    if (window.parent.location.href.includes("contact-us")) window.scrollTo(0, 0);
  };

  const onBackHandler = () => {
    const newSteps = getBackSteps(steps, currentStep);
    setSteps(newSteps);
    setIsEditMode(false);
    if (window.parent.location.href.includes("contact-us")) window.scrollTo(0, 0);
  };

  const enabledSubmit = useMemo(() => {
    return (
      (businessNeedInfo?.businessNeeds?.length ?? 0) > 0 &&
      businessNeedInfo?.businessNeeds?.every((item) => {
        const descriptionLength = item.description.length;
        return descriptionLength >= 80 && descriptionLength <= 1000;
      })
    );
  }, [businessNeedInfo]);

  const getErrorCount = (businessNeeds: BusinessNeed[] | undefined) => {
    let count = 0;
    const errors: string[] = [];

    if (!businessNeeds) {
      return { count: 0, errors: [] };
    }

    if (businessNeeds.length === 0) {
      count++;
      errors.push("At least one business need is required.");
    }

    for (const need of businessNeeds) {
      const descriptionLength = need.description.length;

      if (descriptionLength < 80 || descriptionLength > 1000) {
        count++;
        errors.push(
          `Description for business need "${need.title}" must be between 80 and 1000 characters.`
        );
      }
    }
    return { count, errors };
  };
  const handleAlert = () => {
    setIsSubmitClicked(!isSubmitClicked);
  }

  return (
    <div>
      <div className="lmn-container lmn-d-flex lmn-flex-column left-margin-0" >
        {isSubmitClicked && showAlert && errorCount > 0 && (
          <CustomAlert id="businessNeedsPage-alert" errors={errors} page={"BusinessNeedsPage"} handleChange={handleAlert}/>
        )}
      </div>
      <div ref={divRef}></div>
      <BusinessNeeds />
      <FooterButtonHoc
        submitText="Continue"
        onCancel={onCancelHandler}
        onBack={onBackHandler}
        onSubmit={onSubmitHandler}
      />
    </div>
  );
};

export default BusinessNeedsPage;
